import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'
import { Collapse } from '@mui/material'
import { TextField, Autocomplete } from '../../Base/Form'
import { z } from 'zod'
import * as lib from '../../../../js/lib/lib.js'
import { useSelector } from 'react-redux'
import { postData } from './../../../../js/helpers/apiHelper.js'

export const billingAddressSchema = (dictionary, selectedVehicle) => {
  const isGREngine = selectedVehicle?.isGREngine || false

  return z.object({
    address: isGREngine
      ? z
          .string({
            required_error: dictionary.addressRequiredText,
          })
          .min(1, dictionary.addressRequiredText)
          .min(2, dictionary.addressLengthText)
          .max(100, dictionary.addressLengthText)
      : z.string().optional(),
    streetNumber: isGREngine
      ? z
          .string({
            required_error: dictionary.streetNumberRequiredText,
          })
          .min(1, dictionary.streetNumberRequiredText)
          .max(10, dictionary.streetNumberLengthText)
      : z.string().optional(),
    addressLine: !isGREngine
      ? z
          .string({
            required_error: dictionary.addressLineRequiredText,
          })
          .min(1, dictionary.addressLineRequiredText)
          .min(2, dictionary.addressLineLengthText)
          .max(100, dictionary.addressLineLengthText)
      : z.string().optional(),
    additionalAddressLine: z
      .string({
        required_error: dictionary.additionalAddressLineRequiredText,
      })
      .max(100, dictionary.additionalAddressLineLengthText)
      .optional()
      .or(z.literal('')),
    country: z
      .object(
        { text: z.string(), value: z.string() },
        {
          required_error: dictionary.countryRequiredText,
          invalid_type_error: dictionary.countryRequiredText,
        }
      )
      .refine((val) => !!val?.value && !!val?.text, {
        message: dictionary.countryRequiredText,
      }),
    region: z
      .object(
        { text: z.string(), value: z.string() },
        {
          required_error: dictionary.regionRequiredText,
          invalid_type_error: dictionary.regionRequiredText,
        }
      )
      .refine((val) => !!val?.value && !!val?.text, {
        message: dictionary.regionRequiredText,
      }),
    city: z
      .string({
        required_error: dictionary.cityRequiredText,
      })
      .min(1, dictionary.cityRequiredText)
      .min(2, dictionary.cityLengthText)
      .max(100, dictionary.cityLengthText),
    postalCode: z
      .string({
        required_error: dictionary.postalCodeRequiredText,
      })
      .min(1, dictionary.postalCodeRequiredText),
  })
}

const BillingAddress = forwardRef(
  ({ control, errors, configuration, setValue }, ref) => {
    const { dictionary, countryOptions } =
      window.bookingData || window.modifyData
    const [collapseOpen, setCollapseOpen] = useState(true)
    const [regionOptions, setRegionOptions] = useState([])

    const { selectedVehicle } = useSelector((state) => state.booking)
    const { isGREngine } = selectedVehicle || {}

    useImperativeHandle(ref, () => ({
      expand: () => {
        setCollapseOpen(true)
      },
    }))

    const handleCollapseClick = () => {
      if (lib.scrDevices()) {
        setCollapseOpen(!collapseOpen)
      }
    }

    const getCountryRegions = async () => {
      await new Promise((resolve) => setTimeout(resolve, 0))

      if (!control._formValues.country) {
        setRegionOptions([])
        return
      }

      const { country } = control._formValues

      const payload = { country: country?.value || '' }
      const response = await postData(
        configuration.endpointGetCountryAreas,
        payload
      )

      if (response && response.data && response.data.regions) {
        setRegionOptions(
          response.data.regions.map((region) => ({
            text: region.Name,
            value: region.Code,
          }))
        )
      }
    }

    useEffect(() => {
      // Preselect country based on location from localStorage
      const location = localStorage.getItem('location')
      if (location && countryOptions) {
        const matchingOption = countryOptions.find(
          (option) => option.countryCode === location
        )
        if (matchingOption) {
          setValue('country', matchingOption)
        }
      }
    }, [])

    useEffect(() => {
      const handleResize = () => {
        if (!lib.scrDevices()) {
          setCollapseOpen(true)
        }
      }

      window.addEventListener('resize', handleResize)
      handleResize()

      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }, [])

    return (
      <>
        <div className="s-booking-form__form-group">
          <div
            className="row"
            role="button"
            tabIndex={0}
            onClick={handleCollapseClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleCollapseClick()
              }
            }}
            aria-expanded={collapseOpen}
            aria-label="Toggle billing address section">
            <div className="col-lg-6 mb-24 d-flex align-items-center justify-content-between">
              <span className="s-booking-form__form-group__title">
                {dictionary.billingAddressLabel}
              </span>
              <div className="d-lg-none ">
                <i
                  className={`icon-arrow-down arrow ${
                    collapseOpen ? 'open' : ''
                  }`}
                />
              </div>
            </div>
            <div className="col-lg-6 d-flex justify-content-lg-end align-items-end mb-24">
              <span className="s-booking-form__form-group__required">
                {dictionary.billingAddressRequiredLabel}
              </span>
            </div>
          </div>
          <Collapse in={collapseOpen}>
            {!isGREngine && (
              <div className="mb-24">
                <TextField
                  name={'addressLine'}
                  label={dictionary.billingAddressLabel}
                  control={control}
                  type="text"
                  error={errors.addressLine}
                  // onBlur={() => trigger('addressLine')}
                />
              </div>
            )}
            {isGREngine && (
              <div className="row">
                <div className="col-lg-8 mb-24">
                  <TextField
                    name={'address'}
                    label={dictionary.billingAddressStreetLabel}
                    control={control}
                    type="text"
                    error={errors.address}
                    // onBlur={() => trigger('address')}
                  />
                </div>
                <div className="col-lg-4 mb-24">
                  <TextField
                    name={'streetNumber'}
                    label={dictionary.billingAddressStreetNumberLabel}
                    control={control}
                    type="text"
                    error={errors.streetNumber}
                    // onBlur={() => trigger('streetNumber')}
                  />
                </div>
              </div>
            )}
            <div className="mb-24">
              <TextField
                name={'additionalAddressLine'}
                label={dictionary.billingAddressAdditionalLabel}
                control={control}
                type="text"
                error={errors.additionalAddressLine}
                // onBlur={() => trigger('additionalAddressLine')}
              />
            </div>
            <div className="row">
              <div className="col-lg-6 mb-24">
                <Autocomplete
                  name={'country'}
                  label={dictionary.billingAddressCountryLabel}
                  control={control}
                  options={countryOptions}
                  getOptionLabel={(option) => option.text}
                  error={errors.country}
                  onInputChange={() => {
                    getCountryRegions()
                  }}
                  // onBlur={() => trigger('country')}
                />
              </div>
              <div className="col-lg-6 mb-24">
                <Autocomplete
                  name={'region'}
                  label={dictionary.billingAddressRegionLabel}
                  control={control}
                  options={regionOptions}
                  getOptionLabel={(option) => option.text}
                  error={errors.region}
                  onInputChange={() => {}}
                  // onBlur={() => trigger('region')}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-lg-6 mb-24">
                <TextField
                  name={'city'}
                  label={dictionary.billingAddressCityLabel}
                  control={control}
                  type="text"
                  error={errors.city}
                  // onBlur={() => trigger('city')}
                />
              </div>
              <div className="col-lg-6 mb-24">
                <TextField
                  name={'postalCode'}
                  label={dictionary.billingAddressPostalCodeLabel}
                  control={control}
                  type="text"
                  error={errors.postalCode}
                  // onBlur={() => trigger('postalCode')}
                />
              </div>
            </div>
          </Collapse>
        </div>

        <div className="s-booking-form__separator"></div>
      </>
    )
  }
)

BillingAddress.displayName = 'BillingAddress'

export default BillingAddress
